import React from 'react';
import { createPortal } from 'react-dom';
import { Calendar } from 'lucide-react';
import { formatDateToDDMMYYYY, formatDDMMYYYYInput, parseDDMMYYYYToYMD, todayYMD } from '../utils/date';

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const CALENDAR_WIDTH = 280;
const CALENDAR_HEIGHT = 320;
const VIEWPORT_MARGIN = 12;
const PANEL_GAP = 4;

interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  /** Wenn gesetzt: Kalender per Portal mit diesem z-index (für Modals). */
  menuZIndex?: number;
  disabled?: boolean;
  inputClassName?: string;
}

export function DatePicker({
  value,
  onChange,
  className = '',
  placeholder = 'TT.MM.JJJJ',
  menuZIndex,
  disabled = false,
  inputClassName = '',
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [menuPos, setMenuPos] = React.useState<{ top: number; left: number } | null>(null);
  const [inputText, setInputText] = React.useState(() => (value && value.length >= 10 ? formatDateToDDMMYYYY(value.slice(0, 10)) : ''));
  const [focused, setFocused] = React.useState(false);
  const focusedRef = React.useRef(false);
  focusedRef.current = focused;
  const [viewMonth, setViewMonth] = React.useState(() => {
    if (value && value.length >= 10) {
      const [y, m] = value.slice(0, 10).split('-').map(Number);
      return new Date(y, m - 1, 1);
    }
    return new Date();
  });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const updateMenuPosition = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const left = Math.min(
      Math.max(rect.left, VIEWPORT_MARGIN),
      window.innerWidth - CALENDAR_WIDTH - VIEWPORT_MARGIN,
    );
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    const opensAbove = spaceBelow < CALENDAR_HEIGHT + PANEL_GAP && spaceAbove > spaceBelow;
    const top = opensAbove
      ? Math.max(VIEWPORT_MARGIN, rect.top - CALENDAR_HEIGHT - PANEL_GAP)
      : rect.bottom + PANEL_GAP;
    setMenuPos({ top, left });
  }, []);

  React.useLayoutEffect(() => {
    if (!open || menuZIndex == null) {
      setMenuPos(null);
      return;
    }
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open, menuZIndex, updateMenuPosition]);

  React.useEffect(() => {
    // Don't overwrite while typing — 2-digit years would re-expand via value sync.
    if (focusedRef.current) return;
    setInputText(value && value.length >= 10 ? formatDateToDDMMYYYY(value.slice(0, 10)) : '');
  }, [value]);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatDDMMYYYYInput(e.target.value);
    setInputText(formatted);
    // Only commit live when year has 4 digits so backspacing through JJ doesn't re-commit.
    const yearPart = formatted.split('.')[2] ?? '';
    if (yearPart.length !== 4) return;
    const parsed = parseDDMMYYYYToYMD(formatted);
    if (parsed) onChange(parsed);
  };

  const handleInputBlur = () => {
    setFocused(false);
    const parsed = parseDDMMYYYYToYMD(inputText);
    if (parsed) {
      onChange(parsed);
      setInputText(formatDateToDDMMYYYY(parsed));
      return;
    }
    if (value) setInputText(formatDateToDDMMYYYY(value));
    else setInputText('');
  };

  const handleSelectDay = (ymd: string) => {
    onChange(ymd);
    setInputText(formatDateToDDMMYYYY(ymd));
    setOpen(false);
  };

  const prevMonth = () => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() - 1));
  const nextMonth = () => setViewMonth((d) => new Date(d.getFullYear(), d.getMonth() + 1));

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const startOffset = (firstDay.getDay() + 6) % 7; // Mo = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const days: (number | null)[] = Array(startOffset).fill(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const calendarPanel = (
    <div
      ref={panelRef}
      className="bg-app-surface border border-app-border rounded-xl shadow-lg p-3 min-w-[280px]"
      style={
        menuZIndex != null && menuPos
          ? { position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: menuZIndex, width: CALENDAR_WIDTH }
          : undefined
      }
    >
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={prevMonth}
          className="p-1.5 rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted text-sm font-medium"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-app-text">
          {MONTHS[month]} {year}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="p-1.5 rounded-lg text-app-text-subtle hover:bg-app-hover hover:text-app-text-muted text-sm font-medium"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 text-center">
        {WEEKDAYS.map((w) => (
          <div key={w} className="text-[11px] font-medium text-app-text-subtle py-1">
            {w}
          </div>
        ))}
        {days.map((day, i) => {
          if (day === null) return <div key={`e-${i}`} />;
          const ymd = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          const isSelected = value === ymd;
          const isToday = ymd === todayYMD();
          return (
            <button
              key={ymd}
              type="button"
              onClick={() => handleSelectDay(ymd)}
              className={`
                    w-8 h-8 rounded-lg text-sm font-medium transition-colors
                    ${isSelected ? 'bg-pipedrive-blue text-white' : 'text-app-text-muted hover:bg-app-hover'}
                    ${isToday && !isSelected ? 'ring-1 ring-pipedrive-blue/50' : ''}
                  `}
            >
              {day}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className={`flex h-9 items-center overflow-hidden rounded-md border border-app-border focus-within:border-pipedrive-blue focus-within:ring-2 focus-within:ring-pipedrive-blue/20 ${disabled ? 'bg-app-surface-muted' : 'bg-app-surface'}`}>
        <input
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => setFocused(true)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={`h-9 min-w-0 flex-1 border-0 bg-transparent px-3 text-sm text-app-text outline-none placeholder:text-app-text-subtle disabled:cursor-not-allowed disabled:text-app-text-subtle ${inputClassName}`}
        />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center text-app-text-subtle transition-colors hover:bg-app-hover hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
          title="Datum wählen"
        >
          <Calendar className="w-4 h-4" />
        </button>
      </div>

      {open &&
        (menuZIndex != null && menuPos
          ? createPortal(calendarPanel, document.body)
          : (
            <div className="absolute top-full left-0 mt-1 z-[260]">{calendarPanel}</div>
          ))}
    </div>
  );
}
